import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  X,
  Share2,
  Download,
  Loader2,
  Check,
  Sparkles,
  Camera,
  Upload,
  ArrowLeftRight,
  Instagram,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

/**
 * ShareCard — composites the user's finished-look selfie onto a
 * 1080×1920 (Instagram Story aspect) PNG card with a prominent
 * "Face card never declines with TruBeauté" credit block.
 *
 * Photo sources (in preference order):
 *   1. `snapshots` prop — latest mirror-session snapshot (already in
 *      state on the Routine page, no extra round-trip needed).
 *   2. "Take a photo" — getUserMedia → one-shot selfie.
 *   3. "Upload from library" — file input fallback.
 *
 * The card is rendered in an off-screen <canvas> so the output is
 * pixel-identical across devices. Text placement is defensive against
 * varied photo aspect ratios (we center-crop whatever comes in).
 *
 * Face-shape is deliberately NOT included in the card — face geometry
 * is personal in a way the user didn't opt into publishing. The LOOK
 * they created is theirs to share.
 */
const CARD_W = 1080;
const CARD_H = 1920;
// The top portion reserved for the photo; bottom is the credit panel.
const PHOTO_H = 1280;

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const word of words) {
    const trial = line ? `${line} ${word}` : word;
    if (ctx.measureText(trial).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = trial;
    }
  }
  if (line) lines.push(line);
  return lines;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const drawPhotoPanel = (ctx, img) => {
  // Cover-fit the photo inside the photo panel (0,0 → CARD_W, PHOTO_H).
  const panelAR = CARD_W / PHOTO_H;
  const imgAR = img.width / img.height;
  let sx = 0,
    sy = 0,
    sw = img.width,
    sh = img.height;
  if (imgAR > panelAR) {
    // Image is wider than panel → crop horizontally
    sw = img.height * panelAR;
    sx = (img.width - sw) / 2;
  } else {
    // Taller → crop vertically (bias toward the face: show the top 40%)
    sh = img.width / panelAR;
    sy = (img.height - sh) * 0.3;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, CARD_W, PHOTO_H);

  // Soft gradient at the bottom edge of the photo → blends into the
  // credit panel without a hard seam.
  const fade = ctx.createLinearGradient(0, PHOTO_H - 160, 0, PHOTO_H);
  fade.addColorStop(0, "rgba(28, 18, 22, 0)");
  fade.addColorStop(1, "#1c1216");
  ctx.fillStyle = fade;
  ctx.fillRect(0, PHOTO_H - 160, CARD_W, 160);

  // Subtle top shadow (for the TONIGHT'S LOOK eyebrow readability)
  const topFade = ctx.createLinearGradient(0, 0, 0, 220);
  topFade.addColorStop(0, "rgba(0, 0, 0, 0.55)");
  topFade.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = topFade;
  ctx.fillRect(0, 0, CARD_W, 220);
};

const drawCreditPanel = (ctx, { lookName, subtitle, memberBadge }) => {
  // Credit panel background — warm charcoal/mauve so the photo above
  // transitions elegantly rather than competing.
  const g = ctx.createLinearGradient(0, PHOTO_H, 0, CARD_H);
  g.addColorStop(0, "#1c1216");
  g.addColorStop(0.5, "#5D4050");
  g.addColorStop(1, "#9B6A6C");
  ctx.fillStyle = g;
  ctx.fillRect(0, PHOTO_H, CARD_W, CARD_H - PHOTO_H);

  ctx.textAlign = "center";

  // Look name — italic serif, kept succinct
  if (lookName) {
    ctx.fillStyle = "#FAF6F2";
    ctx.font = "italic 500 72px 'Georgia', serif";
    const lines = wrapText(ctx, lookName, CARD_W - 160).slice(0, 2);
    lines.forEach((ln, i) => {
      ctx.fillText(ln, CARD_W / 2, PHOTO_H + 100 + i * 82);
    });
  }

  // Subtitle quote
  if (subtitle) {
    ctx.fillStyle = "rgba(255, 255, 255, 0.68)";
    ctx.font = "300 30px -apple-system, BlinkMacSystemFont, sans-serif";
    const lines = wrapText(ctx, subtitle, CARD_W - 220).slice(0, 2);
    lines.forEach((ln, i) => {
      ctx.fillText(ln, CARD_W / 2, PHOTO_H + 280 + i * 42);
    });
  }

  // "Face card never declines with TruBeauté" — the credit line.
  // Small leading line + big italic wordmark preserves the visual
  // hierarchy and lets the meme phrase land before the brand reveal.
  ctx.fillStyle = "rgba(255, 246, 242, 0.78)";
  ctx.font = "400 34px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText("Face card never declines with", CARD_W / 2, PHOTO_H + 430);

  ctx.fillStyle = "#FFE6D7";
  ctx.font = "italic 500 104px 'Georgia', serif";
  ctx.fillText("TruBeauté", CARD_W / 2, PHOTO_H + 540);

  // Sparkle rule
  ctx.strokeStyle = "rgba(213, 168, 164, 0.55)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(CARD_W / 2 - 110, PHOTO_H + 575);
  ctx.lineTo(CARD_W / 2 + 110, PHOTO_H + 575);
  ctx.stroke();
  ctx.fillStyle = "#D5A8A4";
  ctx.beginPath();
  ctx.arc(CARD_W / 2, PHOTO_H + 575, 5, 0, Math.PI * 2);
  ctx.fill();

  // Tagline + url
  ctx.fillStyle = "rgba(255, 246, 242, 0.6)";
  ctx.font = "500 26px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(
    "Your digital drag mom · trubeaute.com",
    CARD_W / 2,
    PHOTO_H + 625,
  );

  // Founding Member / Day One stamp — small pill in the top-right
  // corner of the card so it doesn't compete with the brand wordmark.
  // Day One wins over Founding Member when both apply (rarer + gold
  // accent reads better against the violet photo gradient). The user
  // can opt out via the `hideMemberBadge` prop the parent ShareCard
  // sets from their usage data — most users will leave it on, public
  // flex was an explicit ask.
  if (memberBadge) {
    const isDayOne = memberBadge === "day_one";
    const text = isDayOne ? "DAY ONE" : "FOUNDING MEMBER";
    ctx.font = "600 22px -apple-system, BlinkMacSystemFont, sans-serif";
    const measure = ctx.measureText(text);
    const padX = 22;
    const padY = 14;
    const pillW = measure.width + padX * 2 + 28;  // 28 for icon space
    const pillH = 48;
    const pillX = CARD_W - pillW - 56;  // 56px gutter from right edge
    const pillY = 56;                   // 56px from top
    // Gradient fill
    const grad = ctx.createLinearGradient(pillX, pillY, pillX, pillY + pillH);
    if (isDayOne) {
      grad.addColorStop(0, "rgba(212, 161, 74, 0.92)");
      grad.addColorStop(1, "rgba(139, 103, 48, 0.92)");
    } else {
      grad.addColorStop(0, "rgba(93, 64, 80, 0.92)");
      grad.addColorStop(1, "rgba(58, 46, 74, 0.92)");
    }
    ctx.fillStyle = grad;
    // Rounded rect (no roundRect in older canvas — manual)
    const r = pillH / 2;
    ctx.beginPath();
    ctx.moveTo(pillX + r, pillY);
    ctx.lineTo(pillX + pillW - r, pillY);
    ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
    ctx.lineTo(pillX + pillW, pillY + pillH - r);
    ctx.quadraticCurveTo(
      pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH,
    );
    ctx.lineTo(pillX + r, pillY + pillH);
    ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
    ctx.lineTo(pillX, pillY + r);
    ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
    ctx.closePath();
    ctx.fill();
    // Subtle border
    ctx.strokeStyle = isDayOne
      ? "rgba(255, 222, 173, 0.55)"
      : "rgba(213, 168, 164, 0.45)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Sparkle dot to evoke the lucide icon (canvas can't load SVG cheaply)
    ctx.fillStyle = isDayOne ? "#FFEFD5" : "#FFE5DD";
    ctx.beginPath();
    ctx.arc(pillX + padX, pillY + pillH / 2, 5, 0, Math.PI * 2);
    ctx.fill();
    // Label
    ctx.font = "600 22px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = isDayOne ? "#FFEFD5" : "#FFE5DD";
    ctx.fillText(text, pillX + padX + 20, pillY + pillH / 2 + 1);
    // Restore defaults (rest of the card uses centered text + alphabetic baseline)
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
  }
};

const drawFallbackPanel = (ctx) => {
  // No photo chosen — use the photo area for a friendly prompt.
  const g = ctx.createLinearGradient(0, 0, 0, PHOTO_H);
  g.addColorStop(0, "#2C2123");
  g.addColorStop(1, "#5D4050");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, CARD_W, PHOTO_H);
  ctx.fillStyle = "rgba(255, 246, 242, 0.68)";
  ctx.textAlign = "center";
  ctx.font = "300 44px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText("Add your finished look", CARD_W / 2, PHOTO_H / 2 - 12);
  ctx.fillStyle = "rgba(255, 246, 242, 0.4)";
  ctx.font = "400 28px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(
    "use a mirror snapshot, take a selfie, or upload a photo",
    CARD_W / 2,
    PHOTO_H / 2 + 40,
  );
};

const drawTopEyebrow = (ctx) => {
  ctx.fillStyle = "#FFE6D7";
  ctx.font = "600 28px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("TONIGHT'S LOOK", CARD_W / 2, 80);
};

const ShareCard = ({
  open,
  onClose,
  payload = {},
  snapshots = [],
  analyticsPrefix = "look_share_card",
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [rendering, setRendering] = useState(true);
  const [dataUrl, setDataUrl] = useState(null);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [photoPickerMode, setPhotoPickerMode] = useState(null); // null | "camera"
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  // Member-badge stamp on the rendered card. `day_one` wins over
  // `founding_member` when both apply (rarer = better flex). Pulled
  // from /me/usage when the modal opens, then injected into payload.
  const [memberBadge, setMemberBadge] = useState(null);

  useEffect(() => {
    setCanNativeShare(
      typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function",
    );
  }, []);

  // When the modal opens, pick the most recent snapshot as the default
  // photo. User can swap via the "Change photo" controls.
  useEffect(() => {
    if (!open) {
      setPhotoSrc(null);
      setMemberBadge(null);
      return;
    }
    track(`${analyticsPrefix}_opened`);
    // Fetch the user's badge state ONCE per modal-open so the rendered
    // PNG always reflects the current grant — quietly drops to no
    // badge if the user is unauthed / the call fails.
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/me/usage`,
          { withCredentials: true },
        );
        if (data?.is_day_one) setMemberBadge("day_one");
        else if (data?.is_founding_member) setMemberBadge("founding_member");
        else setMemberBadge(null);
      } catch {
        setMemberBadge(null);
      }
    })();
    if (snapshots && snapshots.length > 0) {
      const s = snapshots[snapshots.length - 1];
      // Snapshot API returns base64 without the `data:` prefix
      if (s?.image_base64) {
        const mime = s.mime_type || "image/jpeg";
        setPhotoSrc(`data:${mime};base64,${s.image_base64}`);
      }
    }
  }, [open, snapshots, analyticsPrefix]);

  // Render whenever the photo source or payload changes
  const render = useCallback(async () => {
    if (!open) return;
    setRendering(true);
    await new Promise((r) => setTimeout(r, 10));
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = CARD_W;
    canvas.height = CARD_H;
    const ctx = canvas.getContext("2d");
    try {
      if (photoSrc) {
        const img = await loadImage(photoSrc);
        drawPhotoPanel(ctx, img);
      } else {
        drawFallbackPanel(ctx);
      }
      drawTopEyebrow(ctx);
      drawCreditPanel(ctx, { ...payload, memberBadge });
      const url = canvas.toDataURL("image/png", 0.92);
      setDataUrl(url);
    } catch {
      toast.error(t("shareCard.renderError"));
      setDataUrl(null);
    } finally {
      setRendering(false);
    }
    // `t` is the i18next translator, stable for the language lifetime;
    // omitting it avoids re-rendering the share card mid-export.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, photoSrc, payload, memberBadge]);

  useEffect(() => {
    render();
  }, [render]);

  // ── Photo source actions ─────────────────────────────────────
  const onUploadPick = () => fileInputRef.current?.click();

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoSrc(reader.result);
      track(`${analyticsPrefix}_photo_uploaded`);
    };
    reader.readAsDataURL(file);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1080 } },
        audio: false,
      });
      // IMPORTANT ORDER: switch mode FIRST so the <video> element
      // actually mounts before we touch its ref. The previous
      // setTimeout(50ms) race condition meant on slower devices /
      // throttled tabs the ref was still null when we tried to set
      // srcObject — camera came on but stayed black.
      setPhotoPickerMode("camera");
      setCameraStream(stream);
      track(`${analyticsPrefix}_camera_opened`);
    } catch (err) {
      // Permission-denied is by far the most common cause — call it
      // out explicitly so users know how to fix it.
      const denied =
        err?.name === "NotAllowedError" || err?.name === "SecurityError";
      toast.error(
        denied
          ? t("shareCard.cameraDenied")
          : t("shareCard.cameraFail"),
      );
    }
  };

  // Wire the active MediaStream into the <video> as soon as the
  // element mounts. Doing it from a useEffect (instead of inline
  // after getUserMedia) is what fixes the race — by the time this
  // effect runs, React has already committed the <video> node and
  // `videoRef.current` is guaranteed to be defined.
  useEffect(() => {
    if (photoPickerMode === "camera" && cameraStream && videoRef.current) {
      const v = videoRef.current;
      // Only re-attach if the stream actually changed — avoids the
      // play() promise being interrupted by re-renders.
      if (v.srcObject !== cameraStream) {
        v.srcObject = cameraStream;
        const playPromise = v.play();
        if (playPromise?.catch) {
          // Some mobile browsers reject the first play() if it races
          // with srcObject assignment — swallow the AbortError, the
          // video element re-attempts autoplay on its own thanks to
          // the muted + playsInline attributes.
          playPromise.catch(() => {});
        }
      }
    }
  }, [photoPickerMode, cameraStream]);

  const closeCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
    }
    setPhotoPickerMode(null);
  }, [cameraStream]);

  // Stop the camera stream when the modal closes. Cleanup-on-unmount
  // also fires when the component itself unmounts (route change /
  // parent close) so we don't leak the MediaStream. We deliberately
  // do NOT close on every dependency change — only the explicit
  // "modal closed" transition or true unmount — otherwise re-renders
  // while the camera is open would kill it mid-frame.
  useEffect(() => {
    if (!open && photoPickerMode === "camera") {
      closeCamera();
    }
  }, [open, photoPickerMode, closeCamera]);

  useEffect(() => {
    // Single cleanup on unmount so we never leave the camera light on.
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const tmp = document.createElement("canvas");
    tmp.width = video.videoWidth;
    tmp.height = video.videoHeight;
    const tctx = tmp.getContext("2d");
    // Mirror horizontally so the selfie reads as the user sees themselves
    tctx.translate(tmp.width, 0);
    tctx.scale(-1, 1);
    tctx.drawImage(video, 0, 0);
    const url = tmp.toDataURL("image/jpeg", 0.92);
    setPhotoSrc(url);
    track(`${analyticsPrefix}_selfie_captured`);
    closeCamera();
  };

  // ── Actions ──────────────────────────────────────────────────
  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `trubeaute-look-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    track(`${analyticsPrefix}_downloaded`);
    toast.success(t("shareCard.downloaded"));
  };

  const share = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "trubeaute-look.png", { type: "image/png" });
      const shareData = {
        files: [file],
        title: t("shareCard.shareTitle"),
        text: SHARE_TEXT,
      };
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        track(`${analyticsPrefix}_shared_native`);
      } else {
        download();
      }
    } catch (err) {
      if (err?.name !== "AbortError") download();
    }
  };

  // ── Per-platform share helpers ──────────────────────────────────
  // On mobile, every platform routes through the native OS share
  // sheet (canShare → IG/TikTok pickers appear there). On desktop or
  // browsers without Web Share API, we:
  //   1. Trigger the local download so the user has the image
  //   2. Open the platform's web-compose URL where one exists
  //   3. Show a friendly toast naming the next manual step for IG/TikTok
  //      (which have no web-posting APIs)
  // All client-side. Zero content goes through our servers — there's
  // no upload, no mirror, no thumbnail caching. Mod risk: none.
  const SHARE_TEXT = t("shareCard.shareText");

  const tryNativeShareThen = async (fallback, platform) => {
    if (!dataUrl) return;
    if (canNativeShare) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "trubeaute-look.png", { type: "image/png" });
        const shareData = {
          files: [file],
          title: t("shareCard.shareTitle"),
          text: SHARE_TEXT,
        };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          track(`${analyticsPrefix}_shared_to`, { platform });
          return;
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        // fall through to non-native fallback
      }
    }
    fallback();
  };

  // Instagram doesn't expose a public web-compose / photo-upload URL —
  // their iOS deep-link `instagram://camera` is unreliable and the web
  // app has no upload form. Best we can do: download the image and
  // open instagram.com so the user is one tap away from posting.
  const shareToInstagram = () =>
    tryNativeShareThen(() => {
      download();
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      toast.success(
        "Saved! Instagram is open in a new tab — tap +, then upload from your camera roll ✨",
      );
      track(`${analyticsPrefix}_shared_to`, { platform: "instagram", fallback: true });
    }, "instagram");

  // TikTok's upload URL works on desktop browsers when the user is
  // signed in (drag-and-drop / file picker). On mobile it falls back
  // to the TikTok login page, which is still better than going nowhere.
  const shareToTikTok = () =>
    tryNativeShareThen(() => {
      download();
      window.open("https://www.tiktok.com/upload", "_blank", "noopener,noreferrer");
      toast.success(t("shareCard.tiktokToast"));
      track(`${analyticsPrefix}_shared_to`, { platform: "tiktok", fallback: true });
    }, "tiktok");

  const shareToThreads = () =>
    tryNativeShareThen(() => {
      download();
      // Threads has no public web-compose URL; intent only works in-app.
      // We use the app's universal link, which falls back to web.
      const url = `https://www.threads.com/intent/post?text=${encodeURIComponent(SHARE_TEXT)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(t("shareCard.threadsToast"));
      track(`${analyticsPrefix}_shared_to`, { platform: "threads", fallback: true });
    }, "threads");

  const shareToX = () =>
    tryNativeShareThen(() => {
      download();
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(t("shareCard.xToast"));
      track(`${analyticsPrefix}_shared_to`, { platform: "x", fallback: true });
    }, "x");

  const shareToWhatsApp = () =>
    tryNativeShareThen(() => {
      download();
      const url = `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(t("shareCard.whatsappToast"));
      track(`${analyticsPrefix}_shared_to`, { platform: "whatsapp", fallback: true });
    }, "whatsapp");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText("https://trubeaute.com");
      track(`${analyticsPrefix}_link_copied`);
      toast.success(t("shareCard.linkCopied"));
    } catch {
      toast.error(t("shareCard.linkCopyFail"));
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[75] flex items-center justify-center px-4 py-6 overflow-y-auto bg-[#2C2123]/70 backdrop-blur-sm"
        data-testid="share-card-root"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative max-w-md w-full bg-white rounded-3xl shadow-2xl border border-[#EADDD7] p-6"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 h-8 w-8 rounded-full text-[#8A7D82] hover:text-[#2C2123] hover:bg-[#F2E8E9] transition-colors flex items-center justify-center z-10"
            data-testid="share-card-close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <div className="flex-1 pt-0.5">
              <h3
                className="text-xl font-medium text-[#2C2123] leading-tight"
                data-testid="share-card-title"
              >
                Share tonight's look
              </h3>
              <p className="text-xs text-[#8A7D82] mt-0.5">
                Your finished look · with love from TruBeauté
              </p>
            </div>
          </div>

          {photoPickerMode === "camera" ? (
            <div
              className="relative rounded-2xl overflow-hidden border border-[#EADDD7] bg-black mb-5 aspect-[9/16]"
              data-testid="share-card-camera"
            >
              <video
                ref={videoRef}
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover scale-x-[-1]"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={closeCamera}
                  className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                  data-testid="share-card-camera-cancel"
                  aria-label={t("shareCard.cancelCameraAria")}
                >
                  <X className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="h-16 w-16 rounded-full bg-white border-4 border-white/30 hover:bg-white/90 transition-colors"
                  data-testid="share-card-camera-shutter"
                  aria-label={t("shareCard.takePhotoAria")}
                />
                <span className="w-10" />
              </div>
            </div>
          ) : (
            <>
              <div
                className="relative rounded-2xl overflow-hidden border border-[#EADDD7] bg-[#FDF6F3] mb-3 aspect-[9/16]"
                data-testid="share-card-preview-wrapper"
              >
                {rendering ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
                  </div>
                ) : dataUrl ? (
                  <img
                    src={dataUrl}
                    alt={t("shareCard.previewAlt")}
                    className="absolute inset-0 w-full h-full object-contain"
                    data-testid="share-card-preview"
                  />
                ) : null}
                <canvas
                  ref={canvasRef}
                  width={CARD_W}
                  height={CARD_H}
                  className="hidden"
                  aria-hidden="true"
                />
              </div>

              {/* Photo source picker — shown only if camera isn't open */}
              <div
                className="flex items-center justify-center gap-2 mb-5 flex-wrap"
                data-testid="share-card-photo-sources"
              >
                <button
                  type="button"
                  onClick={openCamera}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B6A6C] hover:text-[#5D4050] bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/40 rounded-full px-3.5 py-2 transition-colors"
                  data-testid="share-card-use-camera"
                >
                  <Camera className="h-3.5 w-3.5" />
                  {t("shareCard.takeSelfie")}
                </button>
                <button
                  type="button"
                  onClick={onUploadPick}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B6A6C] hover:text-[#5D4050] bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/40 rounded-full px-3.5 py-2 transition-colors"
                  data-testid="share-card-upload"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {t("shareCard.uploadPhoto")}
                </button>
                {snapshots && snapshots.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      // Cycle to a different snapshot
                      const current = snapshots.findIndex((s) =>
                        photoSrc?.endsWith(s?.image_base64 || "__no__"),
                      );
                      const next =
                        snapshots[(current + 1) % snapshots.length];
                      if (next?.image_base64) {
                        const mime = next.mime_type || "image/jpeg";
                        setPhotoSrc(`data:${mime};base64,${next.image_base64}`);
                        track(`${analyticsPrefix}_snapshot_cycled`);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B6A6C] hover:text-[#5D4050] bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/40 rounded-full px-3.5 py-2 transition-colors"
                    data-testid="share-card-cycle-snapshot"
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5" />
                    {t("shareCard.tryAnother")}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChosen}
                  className="hidden"
                  data-testid="share-card-file-input"
                />
              </div>

              {/* Actions */}
              <div className="grid grid-cols-1 gap-2">
                {canNativeShare && (
                  <button
                    type="button"
                    onClick={share}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-xl px-5 py-3 text-sm font-medium transition-colors disabled:opacity-50"
                    data-testid="share-card-native-share"
                  >
                    <Share2 className="h-4 w-4" />
                    {t("shareCard.shareDots")}
                  </button>
                )}

                {/* Direct platform shortcuts — render whether or not
                    native share is supported. On mobile they all route
                    through the OS sheet (where the right app picker
                    appears). On desktop they trigger a friendly
                    download + open-platform-compose-tab combo so the
                    user can attach + post in a couple of taps. No
                    content ever passes through our servers. */}
                <div
                  className="grid grid-cols-5 gap-2"
                  data-testid="share-card-platforms"
                >
                  <button
                    type="button"
                    onClick={shareToInstagram}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex flex-col items-center justify-center gap-1 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 hover:bg-[#FAF5F1] text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-2 py-2.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                    data-testid="share-card-platform-instagram"
                    aria-label={t("shareCard.shareInstagramAria")}
                  >
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </button>
                  <button
                    type="button"
                    onClick={shareToTikTok}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex flex-col items-center justify-center gap-1 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 hover:bg-[#FAF5F1] text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-2 py-2.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                    data-testid="share-card-platform-tiktok"
                    aria-label={t("shareCard.shareTiktokAria")}
                  >
                    {/* TikTok mark — Lucide doesn't have one */}
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M19.6 8.2a6.2 6.2 0 0 1-3.4-1V15a5.7 5.7 0 1 1-5-5.6V12a2.7 2.7 0 1 0 2 2.6V2h2.4a4.2 4.2 0 0 0 4 4z"/>
                    </svg>
                    TikTok
                  </button>
                  <button
                    type="button"
                    onClick={shareToThreads}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex flex-col items-center justify-center gap-1 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 hover:bg-[#FAF5F1] text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-2 py-2.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                    data-testid="share-card-platform-threads"
                    aria-label={t("shareCard.shareThreadsAria")}
                  >
                    {/* Threads at-sign style mark */}
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10z"/>
                      <path d="M8.5 9.5c.8-2 2.5-3 4-3 2.5 0 4 1.6 4 4.5 0 3-2 5-5 5-3 0-4.5-2-4.5-4 0-1.6 1.2-2.8 3-2.8 1.5 0 2.7 1 2.7 2.5"/>
                    </svg>
                    Threads
                  </button>
                  <button
                    type="button"
                    onClick={shareToX}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex flex-col items-center justify-center gap-1 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 hover:bg-[#FAF5F1] text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-2 py-2.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                    data-testid="share-card-platform-x"
                    aria-label={t("shareCard.shareXAria")}
                  >
                    {/* X (formerly Twitter) mark */}
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.244 2H21.5l-7.5 8.6L23 22h-6.84l-5.39-6.6L4.6 22H1.34l8.07-9.2L1 2h6.91l4.86 6.02L18.244 2zm-2.4 18h1.9L7.3 4h-2l13.544 16z"/>
                    </svg>
                    X
                  </button>
                  <button
                    type="button"
                    onClick={shareToWhatsApp}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex flex-col items-center justify-center gap-1 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 hover:bg-[#FAF5F1] text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-2 py-2.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                    data-testid="share-card-platform-whatsapp"
                    aria-label={t("shareCard.shareWhatsappAria")}
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={download}
                    disabled={rendering || !dataUrl || !photoSrc}
                    className="inline-flex items-center justify-center gap-2 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-4 py-2.5 text-sm transition-colors disabled:opacity-50"
                    data-testid="share-card-download"
                  >
                    <Download className="h-4 w-4" />
                    {t("shareCard.download")}
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 bg-white border border-[#EADDD7] hover:border-[#9B6A6C]/50 text-[#5C4B51] hover:text-[#2C2123] rounded-xl px-4 py-2.5 text-sm transition-colors"
                    data-testid="share-card-copy-link"
                  >
                    <Check className="h-4 w-4" />
                    {t("shareCard.copyLink")}
                  </button>
                </div>
              </div>

              <p className="text-[11px] text-[#8A7D82] text-center mt-4 leading-relaxed">
                {photoSrc
                  ? t("shareCard.footerWithPhoto")
                  : t("shareCard.footerWithoutPhoto")}
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareCard;
